import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Baby, Users, Bike, IceCream, Castle, Heart } from 'lucide-react';
import SEO from '../components/SEO';

const VacancesFamille = () => {
  return (
    <>
      <SEO 
        title="Vacances en Famille Île de Ré | Villa 8 Personnes avec Spa"
        description="Vacances familiales réussies à l'Île de Ré : villa sécurisée 4 chambres, activités enfants, plages surveillées, pistes cyclables. Séjour parfait pour familles."
        keywords="vacances famille Île de Ré, location villa famille Ré, hébergement 8 personnes Ré, vacances enfants Île de Ré, villa familiale Charente-Maritime"
        ogImage="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200"
        url="/vacances-famille-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/seo/famille.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Heart className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Vacances en Famille à l'Île de Ré
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Des souvenirs inoubliables dans notre villa adaptée aux familles
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">L'Île de Ré, Paradis des Familles</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto mb-8">
            L'<strong>Île de Ré</strong> est LA destination idéale pour des <strong>vacances en famille réussies</strong>. 
            Plages surveillées, pistes cyclables sécurisées, activités variées pour tous les âges : 
            l'île offre un cadre parfait pour créer des souvenirs inoubliables avec vos enfants.
          </p>
        </section>

        {/* Pourquoi la Villa Nomade */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Villa Nomade - Parfaite pour les Familles</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-lg shadow-lg">
              <Users className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Espace pour Toute la Famille</h3>
              <p className="text-gray-700 mb-4">
                4 chambres confortables pouvant accueillir jusqu'à 8 personnes. 2 salles de douche, 
                grand séjour lumineux, cuisine équipée. Chacun a son espace pour se reposer.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-lg shadow-lg">
              <Baby className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Équipements Bébé & Enfants</h3>
              <p className="text-gray-700 mb-4">
                Lit bébé, chaise haute, baignoire bébé fournis sur demande. Jardin entièrement clos et sécurisé. 
                Spa de nage avec alarme conforme aux normes.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 rounded-lg shadow-lg">
              <Bike className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Activités à Proximité</h3>
              <p className="text-gray-700 mb-4">
                Location de vélos enfants (sièges bébé, remorques) à 200m. Plages familiales surveillées à 2 km. 
                Pistes cyclables sécurisées depuis la villa.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-lg shadow-lg">
              <IceCream className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Village Animé</h3>
              <p className="text-gray-700 mb-4">
                Port des Portes-en-Ré à 5 min à pied : marchés, glacier, crêperie, animations estivales. 
                Restaurants adaptés aux familles avec menus enfants.
              </p>
            </div>
          </div>
        </section>

        {/* Activités familiales */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Activités pour Toute la Famille</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <h3 className="text-xl font-semibold mb-2">🚴 Vélo en Famille</h3>
              <p className="text-gray-700">
                Plus de 100 km de pistes cyclables plates et sécurisées. Locations adaptées : vélos enfants, 
                sièges bébé, remorques, follow-me. Parcours familiaux balisés de 5 à 15 km.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <h3 className="text-xl font-semibold mb-2">🏖️ Plages Idéales pour Enfants</h3>
              <p className="text-gray-700">
                <strong>Plage de la Redoute (2 km)</strong> : Surveillée l'été, pente douce, jeux de plage, club enfants 4-12 ans.<br/>
                <strong>Plage du Trousse-Chemise (3 km)</strong> : Eau peu profonde, sable fin, aire de jeux à proximité.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
              <h3 className="text-xl font-semibold mb-2">🦴 Visites Ludiques</h3>
              <p className="text-gray-700">
                <strong>Phare des Baleines</strong> : Montée au sommet (257 marches), musée, vue panoramique (9 km).<br/>
                <strong>Marais salants</strong> : Visite guidée avec récolte de sel, très pédagogique pour les enfants.<br/>
                <strong>Écomusée du Marais Salant</strong> : Animations interactives sur la faune et la flore (Loix, 6 km).
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
              <h3 className="text-xl font-semibold mb-2">🐚 Pêche à Pied</h3>
              <p className="text-gray-700">
                Activité gratuite et ludique à marée basse. Crabes, crevettes, coques : les enfants adorent ! 
                Consultez les horaires de marées et respectez les tailles de capture.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
              <h3 className="text-xl font-semibold mb-2">🎪 Animations Estivales</h3>
              <p className="text-gray-700">
                Marchés nocturnes, concerts gratuits, spectacles de rue, ateliers créatifs pour enfants. 
                Programme disponible dans les offices de tourisme de chaque village.
              </p>
            </div>
          </div>
        </section>

        {/* Conseils pratiques */}
        <section className="mb-16 bg-amber-50 p-8 rounded-lg">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Nos Conseils pour un Séjour Familial Réussi</h2>
          <div className="space-y-4 text-gray-700">
            <div>
              <h3 className="font-semibold mb-2">📅 Meilleure période</h3>
              <p>
                <strong>Juillet-Août :</strong> Plein d'animations, eau à 22°C, mais forte affluence. Réservez 6 mois à l'avance.<br/>
                <strong>Juin ou Septembre :</strong> Température agréable (20-25°C), moins de monde, tarifs plus doux. Notre choix #1 !
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">🎒 À prévoir</h3>
              <p>
                Protection solaire haute (réverbération du sable), casquettes, gourdes, vélos si vous avez (sinon location), 
                épuisettes pour pêche à pied, jeux de plage.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">🏥 Santé & Sécurité</h3>
              <p>
                Pharmacies dans chaque village, médecins de garde, hôpital à La Rochelle (30 min). 
                Notre villa est équipée d'une trousse de premiers secours. Numéros d'urgence affichés.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">🍽️ Courses & Restauration</h3>
              <p>
                Supermarchés ouverts toute l'année. Marchés quotidiens avec produits frais. 
                Nombreux restaurants avec menus enfants. Glacier et crêperie aux Portes (5 min à pied).
              </p>
            </div>
          </div>
        </section>

        {/* Témoignages */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Ils ont Adoré leur Séjour en Famille</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <div className="text-yellow-500">★★★★★</div>
              </div>
              <p className="text-gray-700 italic mb-3">
                "Villa parfaite pour notre famille (2 adultes + 3 enfants). Jardin clos sécurisé, 
                spa de nage adoré par les enfants, tout proche des plages. On reviendra !"
              </p>
              <p className="text-sm text-gray-600">— Sophie & Marc, Juillet 2025</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center gap-2 mb-3">
                <div className="text-yellow-500">★★★★★</div>
              </div>
              <p className="text-gray-700 italic mb-3">
                "Nos enfants ont adoré faire du vélo tous les jours jusqu'aux plages. 
                L'île est tellement adaptée aux familles, on s'est sentis en sécurité partout."
              </p>
              <p className="text-sm text-gray-600">— Famille Dubois, Août 2025</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <Castle className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Créez des Souvenirs Inoubliables en Famille</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Réservez votre séjour à la Villa Nomade et offrez à vos enfants des vacances dont ils se souviendront toute leur vie.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">
                Réserver maintenant
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Découvrir la villa
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": "Vacances Famille Île de Ré",
          "description": "Guide complet pour des vacances familiales réussies à l'Île de Ré avec villa adaptée"
        })}
      </script>
    </>
  );
};

export default VacancesFamille;